import React, {Component} from 'react';
import {Form, Input, Row, Col, InputNumber, Button, notification, Checkbox, Upload, Icon, Tooltip} from 'antd';
import {CheckboxChangeEvent} from 'antd/lib/checkbox';
import {UploadChangeParam} from 'antd/lib/upload';
import {UploadFile} from 'antd/lib/upload/interface';
import {Link} from 'react-router-dom';

import {Region, Club, Membership, MembershipText, Language} from '../../../../interfaces';
import {UpdateMembershipInput} from '../QueryMutation';

require('react-quill/dist/quill.snow.css');
const ReactQuill = require('react-quill');

const FormItem = Form.Item;

interface Properties {
  club: Club;
  region: Region;
  membership: Membership;
  price: any;
  languages: Array<Language>;
  updateMembership: (data: UpdateMembershipInput) => Promise<Membership>;
  isDefaultSelected: boolean;
}

interface State {
  priority: number;
  active: boolean;
  ospAvailable: boolean;
  dspAvailable: boolean;
  isDefault: boolean;
  texts: Array<MembershipText>;
  fileList: Array<UploadFile>;
  rewriteDisable: Array<string>;
}

class MembershipDetail extends Component<Properties, State> {
  constructor(props: Properties) {
    super(props);

    const rewriteDisable: Array<string> = [];
    for (let text of props.membership.texts) {
      if (text.termsAndConditions) {
        rewriteDisable.push(text.language.code);
      }
    }

    this.state = {
      fileList: [],
      priority: props.membership.priority,
      active: props.membership.active,
      ospAvailable: props.membership.ospAvailable,
      dspAvailable: props.membership.dspAvailable,
      isDefault: props.membership.isDefault,
      texts: props.languages
        .map((lang: Language) => {
          const relatedText = props.membership.texts.find(text => text.language.id === lang.id);
          return {
            id: relatedText ? relatedText.id : null,
            title: relatedText ? relatedText.title : '',
            description: relatedText ? relatedText.description : '',
            termsAndConditions: relatedText ? relatedText.termsAndConditions : '',
            language: relatedText ? relatedText.language : lang
          };
        })
        .sort((a: MembershipText, b: MembershipText): number => parseInt(b.id, 10) - parseInt(a.id, 10)),
      rewriteDisable
    };

    this.onUpload = this.onUpload.bind(this);
  }

  UNSAFE_componentWillReceiveProps(props: Properties): void {
    const rewriteDisable: Array<string> = [];
    for (let text of props.membership.texts) {
      if (text.termsAndConditions) {
        rewriteDisable.push(text.language.code);
      }
    }

    this.setState({
      fileList: [],
      priority: props.membership.priority,
      active: props.membership.active,
      ospAvailable: props.membership.ospAvailable,
      dspAvailable: props.membership.dspAvailable,
      isDefault: props.membership.isDefault,
      texts: props.languages
        .map((lang: Language) => {
          const relatedText = props.membership.texts.find(text => text.language.id === lang.id);
          return {
            id: relatedText ? relatedText.id : null,
            title: relatedText ? relatedText.title : '',
            description: relatedText ? relatedText.description : '',
            termsAndConditions: relatedText ? relatedText.termsAndConditions : '',
            language: relatedText ? relatedText.language : lang
          };
        })
        .sort((a: MembershipText, b: MembershipText): number => parseInt(b.id, 10) - parseInt(a.id, 10)),
      rewriteDisable
    });
  }

  updatePriority = (value: React.ReactText): void => {
    const priority = parseInt(value.toString(), 10);
    this.setState({priority});
  };

  updateText = (lang: string, type: string, value: string) => {
    const oldTexts = this.state.texts;
    const texts = [];
    for (let text of oldTexts) {
      if (text.language.code === lang) {
        text[type] = value;
        texts.push(text);
        continue;
      }
      texts.push(text);
    }

    this.setState({texts});
  };

  toggleActive = ({target: {checked}}: CheckboxChangeEvent) => {
    this.setState({active: checked});
  };

  toggleOsp = ({target: {checked}}: CheckboxChangeEvent) => {
    this.setState({ospAvailable: checked});
  };

  toggleDsp = ({target: {checked}}: CheckboxChangeEvent) => {
    this.setState({dspAvailable: checked});
  };
  toggleDefault = ({target: {checked}}: CheckboxChangeEvent) => {
    this.setState({isDefault: checked});
  };

  getFormTexts(): Array<JSX.Element> {
    return this.state.texts.map((text, key) => {
      return (
        <div key={key} style={{marginBottom: 24}}>
          <div
            style={{
              marginBottom: 8,
              paddingBottom: 8,
              borderBottom: '1px solid #999',
              fontWeight: 600,
              textTransform: 'capitalize'
            }}
          >
            {text.language.name}
          </div>
          <FormItem label="Title" validateStatus={!text.title.length ? 'error' : null}>
            <Input type="text" value={text.title} onChange={({target: {value}}) => this.updateText(text.language.code, 'title', value)} />
          </FormItem>
          <FormItem label="Description">
            <ReactQuill
              value={text.description}
              onChange={(value: string) => this.updateText(text.language.code, 'description', value)}
              modules={{
                toolbar: [['bold', 'italic', 'underline'], [{list: 'ordered'}, {list: 'bullet'}], ['clean']]
              }}
            />
          </FormItem>
          <FormItem label="Terms & Conditions">
            <Upload
              action={`${process.env.RESTAPI_URL}/holmesplace/api/backoffice/upload`}
              onChange={this.onUpload(text.language.code)}
              fileList={[...this.state.fileList]}
            >
              <Button>
                <Icon type="upload" /> Upload
              </Button>
            </Upload>
            <Input
              type="text"
              value={text.termsAndConditions}
              onChange={({target: {value}}) => this.updateText(text.language.code, 'termsAndConditions', value)}
              disabled={this.state.rewriteDisable.indexOf(text.language.code) >= 0}
              addonAfter={<Icon type="close" onClick={this.removeFile(text.language.code)} />}
            />
          </FormItem>
        </div>
      );
    });
  }

  validate(): boolean {
    let valid: boolean = true;

    if (!Number.isInteger(this.state.priority)) {
      valid = false;
    }

    for (let text of this.state.texts) {
      if (!text.title) {
        valid = false;
        break;
      }
    }

    return valid;
  }

  handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    if (!this.validate()) {
      notification.error({
        message: 'Form is not valid',
        description: 'Please fill in all required fields.'
      });
      return;
    }

    this.props
      .updateMembership({
        variables: {
          data: {
            id: Number(this.props.membership.id) || null,
            membershipExternalId: this.props.membership.membershipExternalId,
            active: this.state.active || false,
            priority: this.state.priority || 0,
            ospAvailable: this.state.ospAvailable || false,
            dspAvailable: this.state.dspAvailable || false,
            isDefault: this.state.isDefault || false,
            texts: this.state.texts.map(text => ({
              id: text.id || null,
              languageId: text.language.id,
              title: text.title || '',
              description: text.description || '',
              termsAndConditions: text.termsAndConditions || ''
            }))
          }
        }
      })
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Membership has been updated!'
        });
      });
  };

  onUpload = (languageCode: string) => (info: UploadChangeParam) => {
    let fileList: Array<UploadFile> = info.fileList;
    let updateState: boolean = true;
    // 1. Limit the number of uploaded files
    // Only to show two recent uploaded files, and old ones will be replaced by the new
    fileList = fileList.slice(-2);

    // 2. Read from response and show file link
    fileList = fileList.map((file: any) => {
      if (file.response) {
        // Component will show file.url as link
        file.url = file.response.url;
        this.updateText(languageCode, 'termsAndConditions', file.url);
        if (this.state.rewriteDisable.indexOf(languageCode) < 0) {
          this.setState({rewriteDisable: this.state.rewriteDisable.concat([languageCode])});
        }
        updateState = false;
      }
      return file;
    });

    // 3. Filter successfully uploaded files according to response from server
    fileList = fileList.filter((file: any) => {
      if (file.response) {
        if (file.response.status === 'success') {
          this.setState({fileList: []});
          return true;
        }
      }
      return true;
    });
    if (updateState) {
      this.setState({fileList});
    }
  };

  removeFile = (languageCode: string) => () => {
    this.updateText(languageCode, 'termsAndConditions', '');
    this.setState({rewriteDisable: this.state.rewriteDisable.filter(str => str !== languageCode)});
  };

  render(): JSX.Element {
    const {membership, price, club, region, isDefaultSelected} = this.props;

    return (
      <Form onSubmit={this.handleSubmit}>
        <Row style={{padding: 16, background: '#fff'}}>
          <Col span={12} style={{padding: 16}}>
            <FormItem label="Region">
              <Input type="text" disabled value={region.name} />
            </FormItem>
            <FormItem label="Club">
              <Input type="text" disabled value={club.name} />
            </FormItem>
            <FormItem label="Membership code">
              <Input type="text" disabled value={membership.membershipExternalId} />
            </FormItem>
            <FormItem label="Price">
              <Input type="text" disabled value={price && price.price} />
            </FormItem>
            <FormItem label="Priority" validateStatus={!Number.isInteger(this.state.priority) ? 'error' : null}>
              <InputNumber value={this.state.priority} onChange={this.updatePriority} />
            </FormItem>
            <FormItem>
              <Checkbox checked={this.state.active} onChange={this.toggleActive}>
                {'Active'}
              </Checkbox>
            </FormItem>
            <FormItem>
              <Checkbox checked={this.state.ospAvailable} onChange={this.toggleOsp}>
                {'Available in online sales process'}
              </Checkbox>
            </FormItem>
            <FormItem>
              <Checkbox checked={this.state.dspAvailable} onChange={this.toggleDsp}>
                {'Available in digital sales process'}
              </Checkbox>
              <FormItem>
                <Tooltip title={isDefaultSelected ? 'default membership already exist' : ''}>
                  <Checkbox checked={this.state.isDefault} disabled={isDefaultSelected} onChange={this.toggleDefault}>
                    {'Make it Default'}
                  </Checkbox>
                </Tooltip>
              </FormItem>
            </FormItem>
          </Col>
          <Col span={12} style={{padding: 16}}>
            {this.getFormTexts()}
          </Col>
          <Col span={24} style={{textAlign: 'right'}}>
            <Button type="default" style={{marginRight: 8}}>
              <Link to={`/memberships/${this.props.region.id}/${this.props.club.id}`}>Back</Link>
            </Button>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Col>
        </Row>
      </Form>
    );
  }
}

export default MembershipDetail;



// WEBPACK FOOTER //
// ./src/pages/MembershipsPage/components/MembershipDetail/components/MembershipDetail/MembershipDetail.tsx